import React from "react";
import { useObserver } from "mobx-react-lite";
import { Switch, Route, Redirect } from "react-router-dom";
import { CircularProgress } from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import {
  SupervisorAccount as SupervisorAccountIcon,
  Smartphone as SmartphoneIcon,
  Work as WorkIcon,
} from "@material-ui/icons";
import * as Api from "api";
import { useAuthStore } from "stores/AuthStore";
import SigninPage from "./signin/Page";
import AdminUsersPage from "./admin/users/Page";
import AdminDevelopersPage from "./admin/developers/Page";
import AdminAgenciesPage from "./admin/agencies/Page";
import DevelopersAppsPage from "./developers/apps/Page";
import DevelopersReportsPage from "./developers/reports/Page";
import DevelopersDestinationsPage from "./developers/destinations/Page";
import AgenciesAppsPage from "./agencies/apps/Page";
import AgenciesReportsPage from "./agencies/reports/Page";
import AgenciesDestinationsPage from "./agencies/destinations/Page";
import Page from "./Page";
import NotFound from "./NotFound";

const menus = {
  admin: [
    { icon: <SupervisorAccountIcon />, to: "/admin/users", label: "ユーザー" },
    { icon: <SmartphoneIcon />, to: "/admin/developers", label: "開発者" },
    { icon: <WorkIcon />, to: "/admin/agencies", label: "代理店" },
  ],
};

const useStyles = makeStyles({
  loading: {
    height: "100vh",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
});

type RouteConfig = React.ComponentProps<typeof Route> & {
  menus?: React.ComponentProps<typeof Page>["menus"];
  key?: string;
};

export default () => {
  const authStore = useAuthStore();
  const styles = useStyles();

  React.useEffect(() => {
    Api.request.setErrorHandler((error) => {
      if (error.response?.status === Api.Status.UNAUTHORIZED) {
        authStore.signOut();
      }
      return error.response;
    });

    authStore.signIn();
  }, [authStore]);

  const routes = React.useMemo(() => {
    const routes: RouteConfig[] = [];
    const currentUser = authStore.currentUser;

    if (!currentUser) {
      return routes;
    }

    const admin = !currentUser.agency && !currentUser.developer;
    const developer = !currentUser.agency && currentUser.developer;
    const agency = currentUser.agency && !currentUser.developer;

    routes.push({
      path: "/",
      render: () =>
        admin ? (
          <Redirect to="/admin/agencies" />
        ) : developer ? (
          <Redirect to={`/developers/${currentUser.developer?.id}/apps`} />
        ) : agency ? (
          <Redirect to={`/agencies/${currentUser.agency?.id}/apps`} />
        ) : (
          <NotFound />
        ),
    });

    if (admin) {
      routes.push({
        path: "/admin/users",
        component: AdminUsersPage,
        menus: menus.admin,
      });
      routes.push({
        path: "/admin/developers",
        component: AdminDevelopersPage,
        menus: menus.admin,
      });
      routes.push({
        path: "/admin/agencies",
        component: AdminAgenciesPage,
        menus: menus.admin,
      });
    }

    if (admin || developer) {
      routes.push({
        path: "/developers/:developerId(\\d+)/apps",
        component: DevelopersAppsPage,
      });
      routes.push({
        path: "/developers/:developerId(\\d+)/apps/:appId(\\d+)",
        component: DevelopersReportsPage,
      });
      routes.push({
        path:
          "/developers/:developerId(\\d+)/apps/:appId(\\d+)/destinations/:destinationId(\\d+)",
        component: DevelopersDestinationsPage,
      });
    }

    if (admin || agency) {
      routes.push({
        path: "/agencies/:agencyId(\\d+)/apps",
        component: AgenciesAppsPage,
      });
      routes.push({
        path: "/agencies/:agencyId(\\d+)/apps/:appId(\\d+)",
        component: AgenciesReportsPage,
      });
      routes.push({
        path:
          "/agencies/:agencyId(\\d+)/apps/:appId(\\d+)/destinations/:destinationId(\\d+)",
        component: AgenciesDestinationsPage,
      });
    }

    routes.push({
      key: "notfound",
      render: () => <NotFound />,
    });

    return routes;
  }, [authStore.currentUser]);

  return useObserver(() =>
    authStore.currentUser ? (
      <Switch>
        {routes.map(({ component: Component, menus, ...route }) => (
          <Route
            key={route.path?.toString()}
            exact
            render={(props) => (
              <Page menus={menus}>
                {Component ? <Component {...props} /> : null}
              </Page>
            )}
            {...route}
          />
        ))}
      </Switch>
    ) : authStore.verifying ? (
      <main className={styles.loading}>
        <CircularProgress />
      </main>
    ) : (
      <SigninPage />
    )
  );
};
